import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, MessageSquare, Briefcase } from "lucide-react";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";
import Card from "../../components/atoms/Card";

// Curated bank of interview-style scenarios. Each picks a /conversation
// scenario or speaking practice route under the hood. Keeps the user
// inside an interview frame instead of dumping them on the generic list.
const ROUNDS = [
  {
    id: "hr",
    label: "HR / Behavioural",
    spine: "var(--rust)",
    intro: "Tell-me-about-yourself, strengths/weaknesses, conflict, career goals — the questions every HR round covers.",
    starters: [
      "Tell me about yourself.",
      "Why do you want to leave your current role?",
      "What's a weakness you're actively working on?",
      "Walk me through a time you disagreed with a manager.",
      "Where do you see yourself in three years?",
    ],
    to: "/conversations",
  },
  {
    id: "technical",
    label: "Technical / Skill",
    spine: "var(--gold-500)",
    intro: "Explain a project, debug under pressure, defend a design choice. Practice talking about your work without rambling.",
    starters: [
      "Walk me through a project you're proud of.",
      "What's the hardest bug you've debugged?",
      "Why did you choose X over Y in that project?",
      "How would you explain this to a non-technical stakeholder?",
      "What would you do differently if you started over?",
    ],
    to: "/conversation-practice",
  },
  {
    id: "case",
    label: "Case / Problem-solving",
    spine: "var(--sky)",
    intro: "Open-ended business questions. Estimate, structure, decide. The rounds where the answer matters less than the thinking out loud.",
    starters: [
      "How would you estimate the number of EV chargers needed in Bangalore?",
      "Our DAU dropped 8% last week. Walk me through how you'd investigate.",
      "Should we launch this feature in India first or globally?",
      "Design the onboarding flow for a banking app for senior citizens.",
    ],
    to: "/topics",
  },
  {
    id: "stress",
    label: "Stress / Curveball",
    spine: "var(--berry)",
    intro: "Why-should-we-hire-you, what-are-your-salary-expectations, the-interviewer-stays-silent moments. Build calm.",
    starters: [
      "Why should we hire you over the other candidates?",
      "What are your salary expectations?",
      "What would your current manager say about you?",
      "Tell me about a time you failed.",
      "Why is there a gap on your resume?",
    ],
    to: "/speak-practice",
  },
];

export default function InterviewPractice() {
  const [active, setActive] = useState(ROUNDS[0]);

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/career" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Career
        </Link>
        <Chip tone="rust">Interview practice</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Walk in with <em style={{ color: "var(--gold-500)" }}>the words ready.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Practice the four rounds every interview cycles through. Read a question, answer aloud, hear yourself, do it again.
        </p>
      </div>

      {/* Round tabs */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {ROUNDS.map((r) => (
          <button key={r.id} type="button" onClick={() => setActive(r)}
            style={{
              padding: "10px 18px",
              background: active.id === r.id ? "var(--ink-900)" : "#fff",
              color: active.id === r.id ? "var(--paper)" : "var(--fg-2)",
              border: `1px solid ${active.id === r.id ? "var(--ink-900)" : "var(--border-2)"}`,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
            }}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Round detail */}
      <Card spineColor={active.spine}>
        <Chip tone="ink">{active.label}</Chip>
        <p style={{ color: "var(--fg-2)", fontSize: 15.5, lineHeight: 1.6, margin: "14px 0 18px", maxWidth: "60ch" }}>{active.intro}</p>
        <p className="eyebrow" style={{ marginBottom: 10 }}>Starter questions</p>
        <ol style={{ paddingLeft: 22, margin: "0 0 22px" }}>
          {active.starters.map((s, i) => (
            <li key={i} style={{ fontSize: 15, lineHeight: 1.6, color: "var(--fg-1)", padding: "6px 0" }}>{s}</li>
          ))}
        </ol>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to={active.to} style={{ textDecoration: "none" }}>
            <Button variant="primary" iconLeft={<Mic size={14} strokeWidth={1.8} />}>Start practicing</Button>
          </Link>
          <Link to="/write/rephrase" style={{ textDecoration: "none" }}>
            <Button variant="ghost" iconLeft={<MessageSquare size={14} strokeWidth={1.8} />}>Polish a written answer</Button>
          </Link>
        </div>
      </Card>

      {/* Pro tip */}
      <Card paperDeep>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Briefcase size={18} strokeWidth={1.6} color="var(--gold-500)" />
          <span className="eyebrow">Pro tip</span>
        </div>
        <p style={{ color: "var(--fg-1)", fontSize: 15, lineHeight: 1.6, margin: 0, maxWidth: "60ch" }}>
          For behavioural questions, structure your answer as Situation → Action → Result. Three sentences each. Two minutes total. Most candidates ramble for four. The interviewer remembers the candidate who stopped at two.
        </p>
      </Card>
    </div>
  );
}
